import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
const EYE_TRACK_LIMIT = 1.15;
const TILT_LIMIT = 3;
const TILT_DISTANCE = 220;
const FACE_PARALLAX_LIMIT = 1.2;
const NEUTRAL_POSE = {
  cat: "perspective(120px)",
  face: "translate(0px, 0px)",
  pupils: "translate(0px, 0px)"
};

// The docs Pages Router replaces each page component during navigation. Keep
// the decorative pose outside an individual logo instance so a replacement
// header starts from the face the reader was already looking at.
let lastPose = NEUTRAL_POSE;
export default function InteractiveCatchyCat(t0) {
  const $ = _c(22);
  const {
    width: t1,
    height: t2
  } = t0;
  const width = t1 === undefined ? 60 : t1;
  const height = t2 === undefined ? 46 : t2;
  const catRef = useRef(null);
  const earRef = useRef(null);
  const faceDetailsRef = useRef(null);
  const leftPupilRef = useRef(null);
  const rightPupilRef = useRef(null);
  const reducedMotionRef = useRef(false);
  const resetFrameRef = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function reset(t4) {
      const {
        immediate: t5
      } = t4 === undefined ? {} : t4;
      const immediate = t5 === undefined ? false : t5;
      const transition = immediate ? "none" : "transform 1.5s ease-in-out";
      if (resetFrameRef.current) {
        cancelAnimationFrame(resetFrameRef.current);
      }
      catRef.current?.style.setProperty("transition", transition);
      [earRef, faceDetailsRef].forEach(t6 => {
        const {
          current
        } = t6;
        current?.style.setProperty("transition", transition);
      });
      [leftPupilRef, rightPupilRef].forEach(t7 => {
        const {
          current: current_0
        } = t7;
        current_0?.style.setProperty("transition", transition);
      });
      const setNeutralPose = () => {
        resetFrameRef.current = null;
        lastPose = NEUTRAL_POSE;
        catRef.current?.style.setProperty("transform", lastPose.cat);
        [earRef, faceDetailsRef].forEach(_temp);
        [leftPupilRef, rightPupilRef].forEach(_temp2);
      };
      if (immediate) {
        setNeutralPose();
      } else {
        resetFrameRef.current = requestAnimationFrame(setNeutralPose);
      }
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const reset = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function followPointer(event) {
      if (event.pointerType === "touch" || reducedMotionRef.current) {
        return;
      }
      if (resetFrameRef.current) {
        cancelAnimationFrame(resetFrameRef.current);
      }
      const bounds = catRef.current?.getBoundingClientRect();
      if (!bounds) {
        return;
      }
      const horizontal = event.clientX - (bounds.left + bounds.width / 2);
      const vertical = event.clientY - (bounds.top + bounds.height / 2);
      const distance = Math.hypot(horizontal, vertical) || 1;
      const pupilX = horizontal / distance * EYE_TRACK_LIMIT;
      const pupilY = vertical / distance * EYE_TRACK_LIMIT;
      const faceX = horizontal / distance * FACE_PARALLAX_LIMIT;
      const faceY = vertical / distance * FACE_PARALLAX_LIMIT;
      const tiltX = Math.max(-1, Math.min(1, horizontal / TILT_DISTANCE));
      const tiltY = Math.max(-1, Math.min(1, vertical / TILT_DISTANCE));
      lastPose = {
        cat: `perspective(120px) rotateX(${(-tiltY * TILT_LIMIT).toFixed(2)}deg) rotateY(${(tiltX * TILT_LIMIT).toFixed(2)}deg)`,
        face: `translate(${faceX.toFixed(2)}px, ${faceY.toFixed(2)}px)`,
        pupils: `translate(${pupilX.toFixed(2)}px, ${pupilY.toFixed(2)}px)`
      };
      catRef.current?.style.setProperty("transition", "transform 80ms ease-out");
      catRef.current?.style.setProperty("transform", lastPose.cat);
      [earRef, faceDetailsRef].forEach(_temp3);
      [leftPupilRef, rightPupilRef].forEach(_temp4);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const followPointer = t4;
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      const setReducedMotion = () => {
        reducedMotionRef.current = mediaQuery.matches;
        if (mediaQuery.matches) {
          reset({
            immediate: true
          });
        }
      };
      const documentElement = document.documentElement;
      const resetOnLeave = () => reset();
      setReducedMotion();
      window.addEventListener("pointermove", followPointer);
      window.addEventListener("blur", resetOnLeave);
      documentElement.addEventListener("mouseleave", resetOnLeave);
      mediaQuery.addEventListener("change", setReducedMotion);
      return () => {
        window.removeEventListener("pointermove", followPointer);
        window.removeEventListener("blur", resetOnLeave);
        documentElement.removeEventListener("mouseleave", resetOnLeave);
        mediaQuery.removeEventListener("change", setReducedMotion);
        if (resetFrameRef.current) {
          cancelAnimationFrame(resetFrameRef.current);
        }
      };
    };
    t6 = [];
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      transform: lastPose.cat,
      transformOrigin: "50% 55%",
      transition: "transform 180ms ease-out"
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <g fill="#fdc018" ref={earRef} style={{
      transform: lastPose.face
    }}><path d="m51.9 12.4c0-6.2-2.2-12.4-4.6-12.4-2.1 0-5 5.6-10.4 15.9l12.3 5.1c1.3-1.8 2.7-4.1 2.7-8.6z" /><path d="m8.2 21 12.3-5.1c-5.5-10.3-8.3-15.9-10.4-15.9-2.4 0-4.6 6.2-4.6 12.4 0 4.5 1.4 6.8 2.7 8.6z" /></g>;
    t9 = <path d="m51.9 28.1c0-11.5-10.2-18.1-23.2-18.1s-23.2 6.7-23.2 18.1 8.7 16 23.1 16.1h.1.1c14.4 0 23.1-4.7 23.1-16.1z" fill="#fdc018" />;
    $[5] = t8;
    $[6] = t9;
  } else {
    t8 = $[5];
    t9 = $[6];
  }
  let t10;
  let t11;
  let t12;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      transform: lastPose.face
    };
    t11 = <path d="m43.6 13.7c-.6-.4-.6-.9-.1-2 2.1-4.7 3.5-7.6 4.5-7.6 1.2 0 2.4 4.1 2.4 8.3 0 3.6-.8 5.5-1.6 5.5-.2 0-.4-.1-.6-.4-1.5-1.6-2.6-2.5-4.6-3.8z" fill="#f8d841" />;
    t12 = <circle cx="41.5" cy="32" fill="#fff" r="4.5" />;
    $[7] = t10;
    $[8] = t11;
    $[9] = t12;
  } else {
    t10 = $[7];
    t11 = $[8];
    t12 = $[9];
  }
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ellipse cx="41.5" cy="32.3" fill="#000" ref={rightPupilRef} rx="1" ry="2.3" style={{
      transform: lastPose.pupils
    }} />;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <g fill="none" stroke="#f8d841" strokeLinecap="round" strokeWidth="1.5"><path d="m56.2 28c-2 .6-4.8 1.2-8 1.4" /><path d="m48.7 32.8c2.6.7 5.8.5 8-.1" /><path d="m48.4 35.9c2 1.2 4.9 2.1 6.6 2.3" /></g>;
    t15 = <path d="m34.9 36c-1.4 0-3.2.6-6.2.6s-4.8-.6-6.2-.6c-1 0-1.6.5-1.6 1.6s1.2 3.2 7.8 3.2 7.8-2.2 7.8-3.2-.6-1.6-1.6-1.6z" fill="#fff" />;
    t16 = <path d="m9.2 17.5c-.2.3-.4.4-.6.4-.8 0-1.6-2-1.6-5.5 0-4.2 1.2-8.3 2.4-8.3 1 0 2.5 2.9 4.5 7.6.5 1.1.4 1.7-.1 2-2 1.3-3.1 2.2-4.6 3.8z" fill="#f8d841" />;
    t17 = <path d="m28.7 40.8v-4.4" fill="none" stroke="#fdc018" strokeLinecap="round" strokeWidth="1.5" />;
    t18 = <circle cx="15.9" cy="32" fill="#fff" r="4.5" />;
    $[11] = t14;
    $[12] = t15;
    $[13] = t16;
    $[14] = t17;
    $[15] = t18;
  } else {
    t14 = $[11];
    t15 = $[12];
    t16 = $[13];
    t17 = $[14];
    t18 = $[15];
  }
  let t19;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ellipse cx="15.9" cy="32.3" fill="#000" ref={leftPupilRef} rx="1" ry="2.3" style={{
      transform: lastPose.pupils
    }} />;
    $[16] = t19;
  } else {
    t19 = $[16];
  }
  let t20;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <path d="m24.4 36c0 1.5-.3 3-.6 4.5" stroke="#fdc018" />;
    $[17] = t20;
  } else {
    t20 = $[17];
  }
  let t21;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <g ref={faceDetailsRef} style={t10}>{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}<g fill="none" strokeLinecap="round" strokeWidth="1.5">{t20}<g stroke="#f8d841"><path d="m9.3 29.4c-3.2-.2-6-.9-8-1.4" /><path d="m.8 32.7c2.2.6 5.4.8 8 .1" /><path d="m2.5 38.2c1.6-.2 4.5-1.2 6.5-2.3" /></g></g><path d="m31 31.4c-.7 0-1.5.1-2.3.1s-1.5-.1-2.3-.1-1.3.4-1.3 1c0 1.1 1.7 2 3.6 2 2 0 3.6-.9 3.6-2 0-.6-.5-1-1.3-1z" fill="#f78c23" /><path d="m33.6 40.4c-.3-1.4-.5-2.9-.6-4.4" fill="none" stroke="#fdc018" strokeLinecap="round" strokeWidth="1.5" /></g>;
    $[18] = t21;
  } else {
    t21 = $[18];
  }
  let t22;
  if ($[19] !== height || $[20] !== width) {
    t22 = <span aria-hidden="true" className="inline-flex touch-none" ref={catRef} style={t7}><svg height={height} viewBox="0 0 57.4 44.2" width={width} xmlns="http://www.w3.org/2000/svg">{t8}{t9}{t21}</svg></span>;
    $[19] = height;
    $[20] = width;
    $[21] = t22;
  } else {
    t22 = $[21];
  }
  return t22;
}
function _temp4(t0) {
  const {
    current: current_4
  } = t0;
  current_4?.style.setProperty("transition", "transform 80ms ease-out");
  current_4?.style.setProperty("transform", lastPose.pupils);
}
function _temp3(t0) {
  const {
    current: current_3
  } = t0;
  current_3?.style.setProperty("transition", "transform 80ms ease-out");
  current_3?.style.setProperty("transform", lastPose.face);
}
function _temp2(t0) {
  const {
    current: current_2
  } = t0;
  current_2?.style.setProperty("transform", lastPose.pupils);
}
function _temp(t0) {
  const {
    current: current_1
  } = t0;
  current_1?.style.setProperty("transform", lastPose.face);
}